import { useMemo } from 'react';

import { mapAPIAbsencesToAbsenceEntries } from '~/data/absences/data/mapping';
import { Absence } from '~/data/absences/index';
import { AbsenceEntry, AbsenceSupersededReason } from '~/data/absences/types';
import { useGetEmployeeAbsenceData } from '~/data/absences/useGetEmployeeAbsenceData';
import { Enum, EnumListKey, useAllEnums } from '~/data/enums';
import { UseSWROutput } from '~/hooks/useSWR';

type PropsType = {
  employeeId: number;
  includeDeleted?: boolean;
};

type UseEmployeeAbsenceDataOutput = {
  absencesData?: Absence;
  absenceReasons: {
    short: Enum[];
    long: Enum[];
  };
  absenceEntries: AbsenceEntry[];
} & Omit<UseSWROutput<Absence>, 'data'>;

export const useEmployeeAbsenceData = (
  props: PropsType,
): UseEmployeeAbsenceDataOutput => {
  const { employeeId, includeDeleted } = props;

  const { getEnums } = useAllEnums();
  const { data: absencesData, ...rest } = useGetEmployeeAbsenceData(
    {
      employeeId,
    },
    {
      revalidateOnFocus: false,
    },
  );

  const { absenceReasons, absenceEntries } = useMemo(() => {
    const absenceReasons = {
      short: getEnums(EnumListKey.ABSENCE_SHORT_REASON),
      long: getEnums(EnumListKey.ABSENCE_LONG_REASON),
    };

    const absenceEntries: AbsenceEntry[] = absencesData
      ? mapAPIAbsencesToAbsenceEntries(
          [...absencesData.absenceLongs, ...absencesData.absenceShorts],
          absenceReasons.long,
          absenceReasons.short,
        )
      : [];

    const defaultFilteredEntries = !!includeDeleted
      ? absenceEntries
      : absenceEntries.filter(
          (a) =>
            ![AbsenceSupersededReason.DELETE.toString()].includes(
              a?.supersededReason ?? '',
            ),
        );

    return {
      absenceReasons,
      absenceEntries: defaultFilteredEntries,
    };
  }, [absencesData, getEnums, includeDeleted]);

  return {
    absencesData,
    absenceReasons,
    absenceEntries,
    ...rest,
  };
};
